import { callQuery, METRICS_CURRENT_RELATIVE, METRICS_CURRENT_RELATIVE_BOARDED, METRICS_PERCENTILE_RANK, METRICS_PERCENTILE_RANK_BOARDED } from "../../amplify/api-gateway/query";
import { getDayOfWeekUTC, getSqlDateStrFromDateStr } from "../../utils/time";
import { CurrentRelativeMetricName, isMetricCount, MetricName, METRICS_DIMENSION_OPTIONS, METRICS_FORECAST_HORIZON_OPTIONS } from "../filters/metricsFilterOptions";


export const refreshCurrentMetrics = (filterDateOptions: any, setCurrentMetrics: any, setIsLoadingCurrentMetrics: any, setCurrentPercentiles: any) => {
    const promises = [
        callQuery({
            operation: METRICS_CURRENT_RELATIVE,
            // Compare against last week
            comparison_window_start: getSqlDateStrFromDateStr(new Date(new Date().getTime() - 1000 * 60 * 60 * 24 * 7 - 1000 * 60 * 60)),
            comparison_window_end: getSqlDateStrFromDateStr(new Date(new Date().getTime() - 1000 * 60 * 60 * 24 * 7)),
            metric_names: [MetricName.ActivePatients, MetricName.ActiveBoardingTime, MetricName.ActiveLOS],
        }),
        callQuery({
            operation: METRICS_CURRENT_RELATIVE_BOARDED,
            // Compare against last week
            comparison_window_start: getSqlDateStrFromDateStr(new Date(new Date().getTime() - 1000 * 60 * 60 * 24 * 7 - 1000 * 60 * 60)),
            comparison_window_end: getSqlDateStrFromDateStr(new Date(new Date().getTime() - 1000 * 60 * 60 * 24 * 7)),
        }),
    ];

    const promisesPercentile = [
        callQuery({
            operation: METRICS_PERCENTILE_RANK,
            // Compare against past year for the same day/hour periods
            day_of_week: getDayOfWeekUTC(new Date()),
            hour_of_day: new Date().getUTCHours(),
            min_of_hour: new Date().getUTCMinutes(),
            comparison_window_start: getSqlDateStrFromDateStr(new Date(new Date().getTime() - 1000 * 60 * 60 * 24 * 365)),
            comparison_window_end: getSqlDateStrFromDateStr(filterDateOptions.absoluteEnd),
            metric_names: [MetricName.ActivePatients, MetricName.ActiveBoardingTime, MetricName.ActiveLOS],
        }),
        callQuery({
            operation: METRICS_PERCENTILE_RANK_BOARDED,
            // Compare against past year for the same day/hour periods
            day_of_week: getDayOfWeekUTC(new Date()),
            hour_of_day: new Date().getUTCHours(),
            min_of_hour: new Date().getUTCMinutes(),
            comparison_window_start: getSqlDateStrFromDateStr(new Date(new Date().getTime() - 1000 * 60 * 60 * 24 * 365)),
            comparison_window_end: getSqlDateStrFromDateStr(filterDateOptions.absoluteEnd),
        }),
    ];

    Promise.all(promises).then((outputs: any[]) => {
        // console.log("output", outputs);
        const metrics: any = {};
        // Current/relative metrics
        for (let output of outputs[0]) {
            if (output["current_metric_name"] === MetricName.ActivePatients) {
                metrics[CurrentRelativeMetricName.Patient] = {
                    "value": parseFloat(output["current_value"]),
                    "diff": parseFloat(output["difference"]),
                    "diffPercentage": (100 * parseFloat(output["difference"]) / parseFloat(output["last_value"])).toFixed(2),
                };
            } else if (output["current_metric_name"] === MetricName.ActiveLOS) {
                metrics[CurrentRelativeMetricName.LengthOfStay] = {
                    "value": (parseFloat(output["current_value"]) / 3600).toFixed(2),
                    "diff": (parseFloat(output["difference"]) / 3600).toFixed(2),
                    "diffPercentage": (100 * parseFloat(output["difference"]) / parseFloat(output["last_value"])).toFixed(2),
                };
            } else if (output["current_metric_name"] === MetricName.ActiveBoardingTime) {
                metrics[CurrentRelativeMetricName.BoardingTime] = {
                    "value": (parseFloat(output["current_value"]) / 3600).toFixed(2),
                    "diff": (parseFloat(output["difference"]) / 3600).toFixed(2),
                    "diffPercentage": (100 * parseFloat(output["difference"]) / parseFloat(output["last_value"])).toFixed(2),
                };
            }
        }
        // Current/relative metrics (boarded)
        for (let output of outputs[1]) {
            metrics[CurrentRelativeMetricName.BoardedPatients] = {
                "value": parseFloat(output["current_value"]),
                "diff": parseFloat(output["difference"]),
                "diffPercentage": (100 * parseFloat(output["difference"]) / parseFloat(output["last_value"])).toFixed(2),
            };
        }
        setCurrentMetrics(metrics);
    })
    .finally(() => {
        setIsLoadingCurrentMetrics(false);
    });

    Promise.all(promisesPercentile).then((outputs: any[]) => {
        const percentiles: any = {};
        // Percentile metrics
        for (let output of outputs[0]) {
            if (output["metric_name"] === MetricName.ActivePatients) {
                percentiles[CurrentRelativeMetricName.Patient] = {
                    "rank": (100 * parseFloat(output["percent_rank"])).toFixed(0),
                };
            } else if (output["metric_name"] === MetricName.ActiveLOS) {
                percentiles[CurrentRelativeMetricName.LengthOfStay] = {
                    "rank": (100 * parseFloat(output["percent_rank"])).toFixed(0),
                };
            } else if (output["metric_name"] === MetricName.ActiveBoardingTime) {
                percentiles[CurrentRelativeMetricName.BoardingTime] = {
                    "rank": (100 * parseFloat(output["percent_rank"])).toFixed(0),
                };
            }
        }
        // Percentile metrics (boarded)
        for (let output of outputs[1]) {
            percentiles[CurrentRelativeMetricName.BoardedPatients] = {
                "rank": (100 * parseFloat(output["percent_rank"])).toFixed(0),
            };
        }

        setCurrentPercentiles(percentiles);
    });
}

export const updateMetricNamesWithForecastHorizon = (filterOptions: any, metricNames: string[]) => {
    const newNames: string[] = [];
    for (let name of metricNames) {
        if (METRICS_FORECAST_HORIZON_OPTIONS[filterOptions["forecast_horizon"]].id === "0h") {
            newNames.push(name)
        } else {
            newNames.push(name + "_" + METRICS_FORECAST_HORIZON_OPTIONS[filterOptions["forecast_horizon"]].id)
        }
    }
    return newNames;
}

export const removeForecastHorizonFromMetricName = (filterOptions: any, metricName: string) => {
    if (METRICS_FORECAST_HORIZON_OPTIONS[filterOptions["forecast_horizon"]].id === "0h") {
        return metricName;
    } else {
        return metricName.replace("_" + METRICS_FORECAST_HORIZON_OPTIONS[filterOptions["forecast_horizon"]].id, "");
    }
}

export const isStacked = (filterOptions: any, metricName: string) => {
    if (!isMetricCount(metricName)) {
        // Only count type metrics can be stacked
        return false;
    }
    if (METRICS_DIMENSION_OPTIONS[filterOptions["dimension"]].id === "ALL" || filterOptions["dimension_filter_values"].length <= 1) {
        // There's only going to be one line, so no reason to stack
        return false;
    } else {
        return true;
    }
}
